import { Car, CheckCircle2, Circle, Clock, PhoneCall, UserMinus } from 'lucide-react'
import { Chip, type ChipTone } from './Chip'
import type { InterviewProgress, ScorerProgress } from '../../utils/interviews'

// What is still open on an interview: each scorer it needs and the reference
// calls. Every word and every state comes from interview_progress_v1 (migration
// 0274); this only picks a colour. Shown to everybody who can view the card,
// scorer or not, because it carries names and states and never a score.
//
// The chips wrap. Chip is nowrap by default, and on a 390px phone the
// references line ran off the edge mid-sentence ("called by" and no name).
const WRAP = 'whitespace-normal text-left leading-snug'

const SCORER_TONE: Record<ScorerProgress['status'], ChipTone> = {
  submitted: 'green',
  draft: 'amber',
  not_started: 'amber',
}

function ScorerIcon({ status }: { status: ScorerProgress['status'] }) {
  if (status === 'submitted') return <CheckCircle2 className="h-3 w-3" />
  if (status === 'draft') return <Clock className="h-3 w-3" />
  return <Circle className="h-3 w-3" />
}

export function ProgressStrip({ progress }: { progress: InterviewProgress | null | undefined }) {
  if (!progress) return null
  const refs = progress.references
  const refsDone = refs.complete ?? refs.completed >= refs.required

  return (
    <div className="flex flex-wrap gap-1.5" data-testid="interview-progress">
      {progress.unassigned ? (
        <Chip tone="red" className={WRAP}>
          Nobody assigned to score
        </Chip>
      ) : (
        progress.scorers.map((s) => (
          <Chip
            key={s.role}
            // An empty seat is not "still to score": nobody can fill it until
            // someone is assigned, so it reads as a problem, not a wait.
            tone={s.assigned === false ? 'red' : SCORER_TONE[s.status]}
            icon={<ScorerIcon status={s.status} />}
            className={WRAP}
          >
            {s.label}
          </Chip>
        ))
      )}
      {progress.sat_out && (
        <Chip tone="neutral" icon={<UserMinus className="h-3 w-3 shrink-0" />} className={WRAP}>
          {progress.sat_out}
        </Chip>
      )}
      {progress.drive && (
        <Chip
          tone={progress.drive.status === 'submitted' ? 'green' : 'amber'}
          icon={<Car className="h-3 w-3 shrink-0" />}
          className={WRAP}
        >
          {progress.drive.label}
        </Chip>
      )}
      {refs.due && (
        <Chip tone={refsDone ? 'green' : 'amber'} icon={<PhoneCall className="h-3 w-3 shrink-0" />} className={WRAP}>
          {refs.label}
        </Chip>
      )}
    </div>
  )
}
